import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { STATUS_META, STATUS_ORDER, PRIORITY_META, subtaskStats } from '@/constants/projectMeta';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';

const emptyTask = {
  title: '', description: '', status: 'pending', department: '', category: '', project: '',
  pic_id: '', collaborators: [], priority: 'medium', impact: 'medium',
  date_added: '', deadline: '', progress: 0, notes: '', outcome: '',
};

export const ProjectTaskDialog = ({ open, onOpenChange, onSave, task, meta, projects = [], canManage }) => {
  const [form, setForm] = useState(emptyTask);
  const [saving, setSaving] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);
  const selectedCollabs = form.collaborators || [];

  useEffect(() => {
    if (open) setForm(task ? { ...emptyTask, ...task } : { ...emptyTask, department: meta.departments?.[0] || '' });
  }, [open, task, meta.departments]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const autoAvg = subtaskStats(task?.subtasks || []).avg;
  const autoProgress = autoAvg !== null && form.status !== 'completed';

  const toggleCollab = (member) => {
    const exists = (form.collaborators || []).some((c) => c.id === member.id);
    set('collaborators', exists
      ? form.collaborators.filter((c) => c.id !== member.id)
      : [...(form.collaborators || []), { id: member.id, name: member.name }]);
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-task-dialog">
        <DialogHeader>
          <DialogTitle>{task ? 'Edit Project Task' : 'New Project Task'}</DialogTitle>
          <DialogDescription>
            {canManage
              ? 'The selected PIC will get this task inside their My Tasks checklist.'
              : 'You can only update status, progress, notes and outcome.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-[hsl(var(--text-muted))]">Main Task *</label>
            <Input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              disabled={!canManage}
              placeholder="e.g. Revamp Fira Worldwide funnel"
              data-testid="task-title-input"
            />
          </div>

          <div>
            <label className="text-xs text-[hsl(var(--text-muted))]">Project / Event</label>
            <Input
              value={form.project || ''}
              onChange={(e) => set('project', e.target.value)}
              disabled={!canManage}
              list="project-label-options"
              placeholder="e.g. Fira Game 2026, Grand Launch"
              data-testid="task-project-input"
            />
            <datalist id="project-label-options">
              {projects.map((p) => <option key={p.project} value={p.project} />)}
            </datalist>
            {!!projects.length && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {projects.map((p) => (
                  <button key={p.project} type="button" onClick={() => set('project', p.project)}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:bg-white/5"
                    data-testid={`task-project-chip-${p.project}`}>
                    {p.project}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">Department</label>
              <Select value={form.department || ''} onValueChange={(v) => set('department', v)} disabled={!canManage}>
                <SelectTrigger data-testid="task-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {(meta.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">Status</label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">PIC</label>
              <Select value={form.pic_id || ''} onValueChange={(v) => set('pic_id', v)} disabled={!canManage}>
                <SelectTrigger data-testid="task-pic-select"><SelectValue placeholder="Select PIC" /></SelectTrigger>
                <SelectContent>
                  {(meta.members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">Deadline</label>
              <Input type="date" value={form.deadline || ''} onChange={(e) => set('deadline', e.target.value)} disabled={!canManage} data-testid="task-deadline-input" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">Priority</label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v)} disabled={!canManage}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(PRIORITY_META).map((p) => <SelectItem key={p} value={p}>{PRIORITY_META[p].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">Impact</label>
              <Select value={form.impact} onValueChange={(v) => set('impact', v)} disabled={!canManage}>
                <SelectTrigger data-testid="task-impact-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(PRIORITY_META).map((p) => <SelectItem key={p} value={p}>{PRIORITY_META[p].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {canManage && (
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">Collaborators</label>
              <Popover open={collabOpen} onOpenChange={setCollabOpen}>
                <PopoverTrigger asChild>
                  <button type="button"
                    className="w-full mt-1.5 flex items-center justify-between gap-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-left hover:bg-white/5"
                    data-testid="task-collab-trigger">
                    <span className={selectedCollabs.length ? 'text-[hsl(var(--text-primary))] truncate' : 'text-[hsl(var(--text-muted))]'}>
                      {selectedCollabs.length ? `${selectedCollabs.length} selected` : 'Select collaborators'}
                    </span>
                    <ChevronsUpDown size={14} className="shrink-0 opacity-60" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[min(22rem,90vw)] p-0" align="start" data-testid="task-collab-dropdown">
                  <Command>
                    <CommandInput placeholder="Search member…" data-testid="task-collab-search" />
                    <CommandList>
                      <CommandEmpty>No member found.</CommandEmpty>
                      <CommandGroup>
                        {(meta.members || []).map((m) => {
                          const active = selectedCollabs.some((c) => c.id === m.id);
                          return (
                            <CommandItem key={m.id} value={m.name} onSelect={() => toggleCollab(m)} data-testid={`task-collab-${m.id}`}>
                              <Check size={14} className={`mr-2 ${active ? 'opacity-100 text-[hsl(var(--primary))]' : 'opacity-0'}`} />
                              <span className="flex-1 truncate">{m.name}</span>
                              {m.department && <span className="ml-2 text-[10px] text-[hsl(var(--text-muted))]">{m.department}</span>}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {!!selectedCollabs.length && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="task-collab-chips">
                  {selectedCollabs.map((c) => (
                    <button key={c.id} type="button" onClick={() => toggleCollab(c)}
                      className="text-xs px-2 py-1 rounded-full border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
                      data-testid={`task-collab-chip-${c.id}`}>
                      {c.name} <X size={10} className="inline ml-1" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">Progress %</label>
              <Input type="number" min={0} max={100} value={autoProgress ? autoAvg : (form.progress ?? 0)} disabled={autoProgress}
                onChange={(e) => set('progress', Number(e.target.value))} data-testid="task-progress-input" />
              {autoProgress && <p className="text-[10px] mt-1 text-[hsl(var(--text-muted))]">Auto from subtasks average</p>}
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-muted))]">Category</label>
              <Input value={form.category || ''} onChange={(e) => set('category', e.target.value)} disabled={!canManage}
                placeholder="e.g. Campaign, SOP, Training" data-testid="task-category-input" />
            </div>
          </div>

          <div>
            <label className="text-xs text-[hsl(var(--text-muted))]">Notes</label>
            <Textarea rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} data-testid="task-notes-input" />
          </div>
          <div>
            <label className="text-xs text-[hsl(var(--text-muted))]">Outcome</label>
            <Textarea rows={2} value={form.outcome || ''} onChange={(e) => set('outcome', e.target.value)} data-testid="task-outcome-input" />
          </div>
          {task?.pic_name && (
            <Badge variant="outline" className="text-xs">Current PIC: {task.pic_name}</Badge>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="task-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.title.trim()} data-testid="task-save-btn">
            {saving ? 'Saving…' : 'Save Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
